import { useId, type ComponentPropsWithoutRef, type ReactNode, type Ref } from 'react'
import { cn } from '#utils'
import { DoorIcon } from '#icons'
import { IconCircle } from '../IconCircle'
import { panelAnnouncement } from './panelAnnouncement'

export interface DoorAccessPanelProps extends Omit<ComponentPropsWithoutRef<'div'>, 'title'> {
  /** Status icon inside the gradient circle. Defaults to a door. */
  icon?: ReactNode
  title: string
  /**
   * Heading level for the title.
   *
   * Defaults to `h1` because on the SMS-link page this panel IS the page. On mobile it sits
   * under `SmartHeader`, which already renders an `h1` — pass `h2` there rather than shipping
   * two level-one headings, which leaves a screen-reader user with no usable outline.
   */
  titleAs?: 'h1' | 'h2' | 'h3'
  /**
   * The line under the title. Announced politely on change — it swaps between "Checking your
   * location…", "You must be at the club…" and "Slide to unlock", and without a live region a
   * screen-reader user gets no explanation for why the control below is unavailable.
   */
  subtitle?: string
  /**
   * Id to put on the subtitle element, so an action can point at it with `aria-describedby`.
   *
   * Exists because `SlideToUnlock` stays focusable while unavailable specifically so a screen
   * reader can announce WHY — and the why is the subtitle, which lives here while the control
   * is passed in as a prop. The consumer mints one id (`useId`) and hands it to both.
   */
  subtitleId?: string
  /** Advisory message — a partial unlock. Announced politely; NOT an error. */
  status?: string
  /** Failure message. Announced assertively. */
  error?: string
  /** Rendered inside the centred column, under the subtitle — e.g. a code display. */
  children?: ReactNode
  /** Rendered bottom-anchored, below the column — e.g. the slider or the unlock button. */
  action?: ReactNode
  /** Copy under the action. */
  footer?: ReactNode
  /**
   * Announce the whole column as a live region. For terminal confirmations, where the change
   * is the entire panel rather than one line inside it.
   */
  announce?: boolean
  ref?: Ref<HTMLDivElement>
}

/**
 * The shared shell for every door-access state: circle, heading, subtitle, messages, action.
 *
 * Presentation only — no fetching, no state machine. Each surface derives its own props and
 * passes its own action, because the two differ in ways worth keeping apart: mobile slides to
 * unlock and has a PIN branch; the SMS-link page uses a plain button, has no PIN, and no
 * navigation to return to.
 *
 * What is shared is everything that would otherwise be phrased twice — see `copy.ts`.
 *
 * ## Announcements
 *
 * There is ONE always-mounted `sr-only` live region, and every transient message is written
 * into it. That is deliberate and not the obvious construction: a live region has to exist in
 * the DOM *before* its content changes, and NVDA and VoiceOver routinely drop nodes inserted
 * with their content already inside. Conditionally rendering `<p aria-live="polite">{status}</p>`
 * looks correct, tests clean in a snapshot, and announces nothing in a real screen reader.
 *
 * The visible error paragraph carries NO `role="alert"`, even though alerts *are* announced on
 * insertion and it is tempting to keep one for urgency. `panelAnnouncement` returns `error`
 * first, so the same string is already in the region above — and that region goes `assertive`
 * precisely when an error is set. Two announcers, one message, spoken twice.
 *
 * WHAT gets written there is `panelAnnouncement`, which is a separate file because the rule is
 * not obvious: on an `announce` panel the title is the outcome, so it must be spoken alongside
 * the subtitle rather than losing to it.
 *
 * @example
 * ```tsx
 * <DoorAccessPanel
 *   title={doorTitle(doors)}
 *   subtitle="Slide to unlock the door"
 *   error={mutation.isError ? 'Failed to unlock. Please try again.' : undefined}
 *   action={<SlideToUnlock disabled={!available} onUnlock={unlock} />}
 *   footer={<AccessWindowNote minutes={accessWindowMinutes} />}
 * />
 * ```
 */
export function DoorAccessPanel({
  icon,
  title,
  titleAs: Title = 'h1',
  subtitle,
  subtitleId,
  status,
  error,
  children,
  action,
  footer,
  announce = false,
  className,
  ref,
  ...props
}: DoorAccessPanelProps) {
  const liveId = useId()

  const announcement = panelAnnouncement({ error, status, title, subtitle, announce })

  return (
    <>
      {/*
        Mounted unconditionally and never removed — see the note above on why a conditionally
        rendered live region silently fails. `aria-live` is chosen per message kind: an error
        interrupts, everything else waits its turn.
      */}
      <div
        id={liveId}
        role="status"
        aria-live={error ? 'assertive' : 'polite'}
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </div>

      <div
        ref={ref}
        className={cn('flex-1 flex flex-col items-center justify-center px-6', className)}
        {...props}
      >
        <IconCircle className="w-24 h-24 mb-6">
          {icon ?? <DoorIcon className="w-10 h-10 text-white" />}
        </IconCircle>

        <Title className="text-2xl font-bold text-foreground mb-2">{title}</Title>

        {subtitle ? (
          // Visible copy only. The announcement is handled by the region above, so this must
          // NOT carry aria-live — two regions holding the same words double-announce.
          <p
            id={subtitleId}
            className="text-sm text-muted-foreground text-center whitespace-pre-line"
          >
            {subtitle}
          </p>
        ) : null}

        {children ? <div className="mt-8">{children}</div> : null}

        {status ? (
          <p className="text-sm text-butter-warning-700 text-center mt-4">{status}</p>
        ) : null}

        {error ? (
          // Visible copy only — no `role="alert"`, for the same reason the subtitle above
          // carries no `aria-live`. `panelAnnouncement` puts `error` FIRST, so this string is
          // already in the live region, which switches to `assertive` exactly when an error is
          // set. An alert announces on insertion, so keeping both made every failure at a door
          // read out twice, urgently.
          <p className="text-sm text-butter-error-500 text-center mt-4">{error}</p>
        ) : null}
      </div>

      {action || footer ? (
        <div className="px-6 pb-8">
          {action}
          {footer ? (
            <div className="text-sm text-muted-foreground text-center mt-3 mb-6">{footer}</div>
          ) : null}
        </div>
      ) : null}
    </>
  )
}
